import * as React from 'react';
import { useController } from 'react-hook-form';
import Select, { components } from 'react-select';

import api from 'api';
import { useQuery } from 'hooks/query';
import { useCustomFormState } from 'components/Form/Form';
import FieldError from 'components/Form/FieldError';
import classNames from 'classnames';

interface SelectTerritoryFilterProps {
  name: string;
  label?: string;
  placeholder?: string;
  helpText?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  elStyle?: 'fill';
  small?: boolean;
  selectProps?: any;
}

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" viewBox="0 0 24 13.71">
        <path
          d="M12 9.58L21.07.5a1.74 1.74 0 012.43 0 1.72 1.72 0 010 2.43L13.21 13.22a1.73 1.73 0 01-2.42 0L.5 2.93A1.72 1.72 0 01.5.5a1.74 1.74 0 012.43 0z"
          fillRule="evenodd"
          fill="#4c4c4c"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

export const SelectTerritoryFilter: React.FC<SelectTerritoryFilterProps> = ({
  name,
  label,
  placeholder,
  helpText,
  disabled,
  className,
  elStyle,
  small,
  selectProps,
}) => {
  const { hasFloatingLabels, isFiltersForm } = useCustomFormState();
  const { field, fieldState } = useController({ name });
  const [territoriesRes] = useQuery(api.territory.listTerritories, undefined);

  // Extract territories from the response
  const territories = React.useMemo(() => {
    if (!territoriesRes || typeof territoriesRes !== 'object' || !('data' in territoriesRes)) {
      return [];
    }
    return Array.isArray(territoriesRes.data.data) ? territoriesRes.data.data : [];
  }, [territoriesRes]);

  // Convert territories to options format
  const options = React.useMemo(() => {
    return territories.map((territory) => ({
      value: territory.id,
      label: territory.region
        ? `${territory.region.region_name} > ${territory.territory_name}`
        : territory.territory_name,
      data: territory,
    }));
  }, [territories]);

  const selectedOption = options.find((option) => option.value === field.value) || null;

  const inputProps = {
    ...selectProps,
    value: selectedOption,
    isDisabled: disabled,
    placeholder: placeholder || label,
    options,
    onChange: (option: any) => {
      field.onChange(option ? option.value : null);
    },
    onBlur: () => {
      field.onBlur();
    },
    styles: {
      control: (props: any) => ({
        ...props,
        border: '1px solid rgba( 0, 0, 0, .05 )',
        boxShadow: '0 1px .1875rem rgba(0, 0, 0, .05)',
        backgroundColor: '#fafafa',
        ':hover': {
          borderColor: 'rgba(41, 127, 203, .2)',
        },
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      indicatorsContainer: (props: any) => ({
        ...props,
        marginRight: '.5rem',
      }),
      valueContainer: (props: any) => ({
        ...props,
        paddingLeft: '.875rem',
        paddingTop: 0,
        paddingBottom: 0,
      }),
      singleValue: (props: any) => ({
        ...props,
        top: small ? '0.6rem' : '1.22917rem',
        transform: 'none',
        fontSize: small ? '.875rem' : '1rem',
        fontWeight: small ? '400' : '600',
        lineHeight: '1.25rem',
      }),
      input: (props: any) => ({
        ...props,
        paddingTop: 0,
        paddingBottom: 0,
        marginTop: 0,
        marginBottom: 0,
        input: {
          minHeight: 'auto !important',
          paddingTop: small ? '0.6rem !important' : '1.22917rem !important',
          paddingBottom: small ? '0.65rem !important' : '.39583rem !important',
          boxShadow: 'none !important',
        },
      }),
      menu: (props: any) => ({
        ...props,
        zIndex: 6,
      }),
      menuList: (props: any) => ({
        ...props,
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: small ? '.875rem' : '1rem',
      }),
      placeholder: (props: any) => ({
        ...props,
        fontSize: small ? '.875rem' : '1rem',
      }),
    },
    components: { DropdownIndicator },
    theme: (theme: any) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#57ae55',
        primary25: 'rgba(0, 0, 0, .05)',
        primary50: 'rgba(0, 0, 0, .1)',
      },
    }),
  };

  const error = fieldState.error;

  return (
    <div
      className={classNames(
        'c-form-element',
        'c-form-element--select',
        'c-form-element--react-select',
        {
          [`c-form-element--style-${elStyle}`]: elStyle,
          'c-form-element--small': small,
          'c-form-element--error': typeof error !== 'undefined',
        },
        className
      )}
    >
      {typeof label !== 'undefined' ? (
        hasFloatingLabels ? (
          <div className="c-form-element__field">
            <div className="fl-wrap fl-wrap-input">
              <label htmlFor={name} className={classNames('c-form-label', { 'c-filters__title': isFiltersForm })}>
                {label}
              </label>
              <Select {...inputProps} />
            </div>
          </div>
        ) : (
          <>
            <label htmlFor={name} className={classNames('c-form-label', { 'c-filters__title': isFiltersForm })}>
              {label}
            </label>
            <div className="c-form-element__field">
              <Select {...inputProps} />
            </div>
          </>
        )
      ) : (
        <div className="c-form-element__field">
          <Select {...inputProps} />
        </div>
      )}
      <FieldError error={error} />
      {typeof helpText !== 'undefined' && <p className="c-note">{helpText}</p>}
    </div>
  );
};
